import {Component, OnInit, Optional} from '@angular/core';
import {Translatable, TranslationService} from '@ngmedax/translation';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {LayoutService} from '@ngmedax/layout';
import {CustomFieldOption, CustomFieldType, CustomFieldValidation} from '@ngmedax/master-data';

import {SettingsClientCustomService} from '../../services/settings-client-custom.service';
import {TRANSLATION_CRUD_SCOPE} from '../../../../constants';
import {KEYS} from '../../../../translation-keys';
import {ClientCustomFieldDefinition} from '../../../../types';


// hack to inject decorator declarations. must occur before class declaration!
export interface SettingsClientCustomCrudComponent extends Translatable {}

@Component({
  selector: 'app-settings-client-custom-crud',
  templateUrl: './settings-client-custom-crud.component.html',
  styleUrls: ['./settings-client-custom-crud.component.css']
})
@Translatable({scope: TRANSLATION_CRUD_SCOPE, keys: KEYS})
export class SettingsClientCustomCrudComponent implements OnInit {
  public uid: string;
  public key: string = '';
  public label: string = '';
  public type: CustomFieldType = null;
  public options: CustomFieldOption[] = [];
  public validation: CustomFieldValidation = {regex: '', regexMod: '', hint: ''};
  public required: boolean = false;
  public isEditMode = false;

  /**
   * Injects dependencies.
   */
  public constructor(
    @Optional() private translationService: TranslationService,
    private layoutService: LayoutService,
    public customFieldService: SettingsClientCustomService,
    public activeModal: NgbActiveModal
  ) {
  }

  public ngOnInit() {
    this.uid = this.generateUUID();
  }

  /**
   * Checks if the form can be saved.
   */
  public canSave(): boolean {
    if (!this.key || !this.label || !this.type) {
      return false;
    }

    // validate key format (must be a valid js identifier)
    if (!/^[a-zA-Z][a-zA-Z0-9_]{0,63}$/.test(this.key)) {
      return false;
    }

    // if type is select, at least one option is required
    if (this.type === 'select') {
      if (this.options.length === 0) {
        return false;
      }
      // all options must have a value
      for (const opt of this.options) {
        if (!opt.value) {
          return false;
        }
      }
    }

    // validate regex if provided for text type
    if (this.type === 'text' && this.validation.regex) {
      try {
        new RegExp(this.validation.regex, this.validation.regexMod || '');
      } catch (e) {
        return false;
      }
    }

    return true;
  }

  /**
   * Closes the modal.
   */
  public onCancel() {
    this.activeModal.close();
  }

  /**
   * Saves the definition.
   */
  public async onSave() {
    this.layoutService.showPreloader();

    try {
      const definition: ClientCustomFieldDefinition = {
        uid: this.uid,
        key: this.key,
        label: this.label,
        type: this.type,
        required: this.required
      };

      // only include options for select type
      if (this.type === 'select') {
        definition.options = this.options;
      }

      // include validation for text type (regex) or if required with hint
      if (this.type === 'text' && (this.validation.regex || this.validation.hint)) {
        definition.validation = this.validation;
      } else if (this.required && this.validation.hint) {
        definition.validation = {hint: this.validation.hint};
      }

      if (this.isEditMode) {
        await this.customFieldService.updateCustomField(definition);
      } else {
        await this.customFieldService.createCustomField(definition);
      }

      this.customFieldService.onDefinitionSaved().emit();
      alert(this._(KEYS.CRUD.SUCCESSFULLY_SAVED_DEFINITION));
      this.activeModal.close();
    } catch (error) {
      console.error(error);
      alert(this._(KEYS.CRUD.ERROR_SAVING_DEFINITION));
    }

    this.layoutService.hidePreloader();
  }

  /**
   * Loads a definition by uid for editing.
   *
   * @param uid
   */
  public async load(uid: string) {
    this.layoutService.showPreloader();

    try {
      const definition = await this.customFieldService.loadCustomField(uid);

      if (!definition) {
        alert(this._(KEYS.CRUD.FOUND_NO_DEFINITION_BY_ID) + ' ' + uid);
        this.layoutService.hidePreloader();
        this.activeModal.close();
        return;
      }

      this.uid = definition.uid;
      this.key = definition.key;
      this.label = definition.label;
      this.type = definition.type;
      this.options = definition.options || [];
      this.validation = definition.validation || {regex: '', regexMod: '', hint: ''};
      this.required = definition.required || false;
      this.isEditMode = true;
    } catch (error) {
      console.error(error);
      alert(this._(KEYS.CRUD.ERROR_SAVING_DEFINITION));
      this.activeModal.close();
    }

    this.layoutService.hidePreloader();
  }

  /**
   * Adds a new empty option for select type.
   */
  public addOption() {
    this.options.push({value: '', label: ''});
  }

  /**
   * Syncs label with value when option changes.
   *
   * @param option
   */
  public onOptionChange(option: CustomFieldOption) {
    option.label = option.value;
  }

  /**
   * Removes an option at the given index.
   *
   * @param index
   */
  public removeOption(index: number) {
    this.options.splice(index, 1);
  }

  /**
   * Handles type change - clears options/validation based on type.
   */
  public onTypeChange() {
    if (this.type !== 'select') {
      this.options = [];
    }
    if (this.type !== 'text') {
      this.validation = {regex: '', regexMod: '', hint: ''};
    }
  }

  /**
   * Checks if the current regex is valid.
   */
  public isRegexValid(): boolean {
    if (!this.validation.regex) {
      return true;
    }
    try {
      new RegExp(this.validation.regex, this.validation.regexMod || '');
      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * Checks if the key is valid (starts with letter, only letters/digits/underscores, max 64 chars).
   */
  public isKeyValid(): boolean {
    if (!this.key) {
      return false;
    }
    return /^[a-zA-Z][a-zA-Z0-9_]{0,63}$/.test(this.key);
  }

  /**
   * Generates a uuid.
   */
  private generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }
}
